import { getRouteProjectDetails, getRouteProjects } from "@/zapi-frontend/shared/const/router";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { projectLanguageToLng } from "@/zapi-frontend/shared/i18n";
import { useProjectStore, type TCoverPhoto } from "@/zapi-frontend/stores/project";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { GET_PROJECTS_LIST_KEY } from "@/zapi-frontend/shared/services/useGetProjectsList";
import { useCreateProjectStore, useProjectDataForCaseBrief } from "@/zapi-frontend/stores/createProject";
import $api from "@/zapi-frontend/shared/api/axios";
import { useProjectSetupStore } from "@/zapi-frontend/stores/projectSetup";
import { useMeStore } from "@/zapi-frontend/shared/hooks/useMe";
import { useFeatureFlag } from "@/zapi-frontend/shared/hooks/useFeatureFlag";
import { useChatStore } from "@/zapi-frontend/stores/chatStore";
import { reserveDraftUpload, usePreUploadDocuments } from "../model/usePreUploadDocuments";
import { hasAdminPrivileges, hasEditAccess } from "@/zapi-frontend/shared/utils/permissions";
import { useDebounce } from "@/zapi-frontend/shared/utils/useDebounce";
import { isValidUrl } from "@/zapi-frontend/shared/utils/url";
import {
  CreateProjectChat,
  CreateProjectChatRef,
  ChatUploadedFile,
  CaseBriefCompleteness,
  ChatStep,
  normalizeCaseBriefLanguageUserMessages,
  type TFrameProgress
} from "@/zapi-frontend/widgets/CreateProjectChat";
import { useCaseBriefChatStore } from "@/zapi-frontend/stores/caseBriefChatStore";
import { useShallow } from "zustand/react/shallow";
import { parseVaultItemsMessage } from "@/zapi-frontend/widgets/CreateProjectChat/ui/CaseBriefChatMessage";
import { useOrganizationStore } from "@/zapi-frontend/stores/organization";
import { withSectionsFrom, type TBriefSections } from "@/zapi-frontend/widgets/CreateProjectChat/lib/briefSections";
import { useLanguages, LANGUAGE_CODE_TO_VALUE } from "@/zapi-frontend/shared/services/useLanguages";
import {
  defaultCaseBrief,
  getDefaultCaseBrief
} from "@/zapi-frontend/widgets/CreateProjectChat/const/defaultCaseBrief";
import { useProjectDimensions } from "@/zapi-frontend/shared/services/useProjectDimensions";
import { caseBriefDocumentPath, openVaultDocument } from "@/zapi-frontend/shared/lib/openVaultDocument";
import { removeTimestampPrefix } from "@/zapi-frontend/shared/lib/splitFileName";
import { NewProjectView } from "./NewProject/NewProjectView";
import { useOrganizationTags } from "./NewProject/useOrganizationTags";

const ALL_LANGUAGE_OPTIONS = [
  { label: "English", value: "english" },
  { label: "Japanese", value: "japanese" }
];
// The project card names each language in its own script.
const NATIVE_LANGUAGE_LABELS: Record<string, string> = { english: "English", japanese: "日本語" };

const triggerAutoSetup = async (projectId: string, model?: string, prefill = true): Promise<number | null> => {
  try {
    const response = await $api.post<{ taskId: number }>(`/projects/${projectId}/auto-setup`, {
      model,
      prefill
    });
    return response.data.taskId;
  } catch (error) {
    console.error("Failed to trigger auto-setup:", error);
    throw error;
  }
};

const CHAT_STATE_SAVE_DELAY_MS = 200;
type TChatStoreState = ReturnType<typeof useCaseBriefChatStore.getState>;
type TChatStateChanges = { chat: boolean; files: boolean; dimensions: boolean };

export function CreateProjectPage() {
  const { t } = useTranslation("createProject");
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { addProject } = useProjectStore();
  const { setSetupTask } = useProjectSetupStore();
  const {
    addExternalDocument,
    uploadedDocuments,
    clearDocuments,
    uploadDocuments,
    addWebLink: addWebLinkFromHook,
    removeDocument: removeDocumentFromHook,
    pendingUploadNames
  } = usePreUploadDocuments();
  const currentOrganizationId = useOrganizationStore((s) => s.currentOrganizationId);
  const removeDocument = useCallback(
    (documentId: number) => {
      removeDocumentFromHook(documentId, {
        vectorStoreId: useCaseBriefChatStore.getState().vectorStoreId,
        organizationId: currentOrganizationId
      });
      const current = useCaseBriefChatStore.getState().uploadedFiles;
      const next = current.filter((f) => f.documentId !== documentId);
      if (next.length !== current.length) {
        useCaseBriefChatStore.getState().setUploadedFiles(next);
      }
    },
    [removeDocumentFromHook, currentOrganizationId]
  );
  const addWebLink = useCallback(
    async (url: string, lang?: string) => {
      let normalizedUrl = url.trim();
      if (!normalizedUrl) return;
      if (!normalizedUrl.startsWith("http://") && !normalizedUrl.startsWith("https://")) {
        normalizedUrl = `https://${normalizedUrl}`;
      }
      if (!isValidUrl(normalizedUrl)) {
        toast.error(t("newProject.linkInvalid"));
        return;
      }

      const result = await addWebLinkFromHook(normalizedUrl, lang);
      const current = useCaseBriefChatStore.getState().uploadedFiles;
      const next = [
        ...current,
        {
          documentId: result.id,
          fileName: result.name,
          fileSize: result.size,
          webUrl: result.webUrl
        }
      ];
      useCaseBriefChatStore.getState().setUploadedFiles(next);
      return result;
    },
    [addWebLinkFromHook, t]
  );
  const selectedModel = useChatStore((s) => s.modelName);
  const { isLabs, role, accessMode, uiLanguage } = useMeStore();
  const hasEdit = hasEditAccess(role, accessMode);
  const readOnly = !hasEdit;

  const setProjectDataForBrief = useCreateProjectStore((s) => s.setProjectData);
  const clearProjectData = useCreateProjectStore((s) => s.clearProjectData);

  useEffect(() => {
    if (!role) return;
    // read-only users may VIEW an existing draft, but cannot start a new project
    if (!hasEdit && !searchParams.get("caseBriefId")) navigate(getRouteProjects(), { replace: true });
  }, [role, hasEdit, searchParams, navigate]);

  const chatRef = useRef<CreateProjectChatRef | null>(null);
  const [caseBriefContent, setCaseBriefContentState] = useState(defaultCaseBrief);
  // Updated with every change rather than on render, so a save always pairs the brief on screen
  // with the server copy from the same moment.
  const caseBriefContentRef = useRef(caseBriefContent);
  const setCaseBriefContent = useCallback((next: string) => {
    caseBriefContentRef.current = next;
    setCaseBriefContentState(next);
  }, []);
  const [caseBriefId, setCaseBriefId] = useState<number | null>(null);
  const projectData = useProjectDataForCaseBrief(caseBriefId);
  const setProjectData = useCallback(
    (data: typeof projectData) => {
      if (caseBriefId === null) return;
      setProjectDataForBrief(caseBriefId, data);
    },
    [caseBriefId, setProjectDataForBrief]
  );
  const caseBriefCreated = useRef(false);
  const [completeness, setCompleteness] = useState<CaseBriefCompleteness | null>(null);
  // Set once Leo calls the frame ready, so the launch dialog never says otherwise.
  const [essentialsCovered, setEssentialsCovered] = useState(false);
  const handleEssentialsCovered = useCallback(() => setEssentialsCovered(true), []);
  const [chatStep, setChatStep] = useState<ChatStep>("language");
  const [language, setLanguage] = useState("english");
  // `language` defaults to English before the user picks one, so until then the page shows the
  // user's interface language instead.
  const displayLanguage = chatStep === "language" ? uiLanguage ?? language : language;
  // The interface stays in the user's language (useUserLanguageSync, at the app root). This is
  // only the language of the content on this page — the brief and the chat — and it is applied
  // as a `lang` attribute on those containers so the browser picks the right CJK glyph forms.
  const contentLng = projectLanguageToLng(displayLanguage);
  // Files added from the Vault panel or attached in the chat belong to the draft like the setup's own
  // uploads: they count against its 100 MB, the chat can search them, and they are saved with it.
  // Resolves with the files that were added and the ones that weren't (over the limit, or failed).
  const uploadDocumentsToDraft = useCallback(
    async (files: File[], lang?: string) => {
      const { accepted, rejected, release } = reserveDraftUpload(files);
      if (rejected.length > 0) toast.error(t("draftUploadLimit", { names: rejected.map((f) => f.name).join(", ") }));
      if (accepted.length === 0) return { created: [], notUploaded: rejected };
      const { caseBriefId: draftId, vectorStoreId } = useCaseBriefChatStore.getState();
      const { uploaded, failed } = await uploadDocuments(
        accepted,
        { language: lang, caseBriefId: draftId, vectorStoreId, organizationId: currentOrganizationId },
        (file, created) => {
          const store = useCaseBriefChatStore.getState();
          if (created && !store.uploadedFiles.some((f) => f.documentId === created.id)) {
            store.setUploadedFiles((prev) => [
              ...prev,
              { documentId: created.id, fileName: created.name, fileSize: created.size }
            ]);
          }
          release(file);
        }
      );
      if (failed.length > 0) toast.error(t("failedUpload", { name: failed.map((f) => f.name).join(", ") }));
      return { created: uploaded, notUploaded: [...rejected, ...failed] };
    },
    [uploadDocuments, currentOrganizationId, t]
  );
  const [visibility, setVisibility] = useState<"organization" | "private">("organization");
  const defaultPrivate = useFeatureFlag("default-private-projects");
  const claudeGeneration = useFeatureFlag("claude-code-generation");
  const documentReading = useFeatureFlag("case-brief-document-reading");
  const serverBriefRef = useRef<string | null>(null);
  const briefSaveFields = useCallback((): { mdContent?: string; baseMdContent?: string } => {
    if (!documentReading) return { mdContent: caseBriefContentRef.current };
    // Without the copy it started from, a save can't tell the user's edits from stale text and
    // would overwrite what was written meanwhile (e.g. the confirmed decision), so it leaves the
    // brief alone.
    if (serverBriefRef.current === null) return {};
    return { mdContent: caseBriefContentRef.current, baseMdContent: serverBriefRef.current };
  }, [documentReading]);
  const visibilityTouched = useRef(false);
  const [initialGeneratingStatus, setInitialGeneratingStatus] = useState<string | null>(null);

  useEffect(() => {
    if (!visibilityTouched.current && defaultPrivate !== undefined)
      setVisibility(defaultPrivate ? "private" : "organization");
  }, [defaultPrivate]);

  const [projectName, setProjectName] = useState("");
  const [projectDescription, setProjectDescription] = useState("");
  const projectMetaGenerated = useRef(false);
  // The last generated name and description: a field still holding one of them was never edited
  // by the user, so a better suggestion may replace it.
  const generatedMetaRef = useRef({ name: "", description: "" });

  // The setup form edits page state only, so the latest values are read from here, not the store.
  const currentMetaRef = useRef({ name: projectName, description: projectDescription });
  currentMetaRef.current = { name: projectName, description: projectDescription };

  // The project row does not exist yet on this page, so the picked photo rides along
  // as a data URL and is sent with the create request.
  const [logoBase64, setLogoBase64] = useState<string | null>(null);
  // Fetched for the subject the brief describes; a photo the user picks replaces it.
  const [coverPhoto, setCoverPhoto] = useState<TCoverPhoto | null>(null);
  const coverPhotoQueryRef = useRef<string | null>(null);
  const logoBase64Ref = useRef(logoBase64);
  logoBase64Ref.current = logoBase64;

  const requestCoverPhoto = useCallback((briefId: number, query: string) => {
    if (coverPhotoQueryRef.current === query || logoBase64Ref.current) return;
    coverPhotoQueryRef.current = query;
    $api
      .post<{ photo: TCoverPhoto | null }>("/chat/create-project-chat/cover-photo", { caseBriefId: briefId, query })
      .then((res) => {
        if (res.data.photo && !logoBase64Ref.current) setCoverPhoto(res.data.photo);
      })
      .catch((err) => console.error("Cover photo fetch failed:", err));
  }, []);

  const applyGeneratedMeta = useCallback(
    (meta: { name: string | null; description: string | null; photoQuery?: string | null }) => {
      const untouched = (value: string, generated: string) => !value.trim() || value === generated;
      if (meta.name && untouched(currentMetaRef.current.name, generatedMetaRef.current.name)) {
        generatedMetaRef.current.name = meta.name;
        setProjectName(meta.name);
        useCaseBriefChatStore.getState().setProjectName(meta.name);
        projectMetaGenerated.current = true;
      }
      if (meta.description && untouched(currentMetaRef.current.description, generatedMetaRef.current.description)) {
        generatedMetaRef.current.description = meta.description;
        setProjectDescription(meta.description);
        useCaseBriefChatStore.getState().setProjectDescription(meta.description);
        projectMetaGenerated.current = true;
      }
      const briefId = useCaseBriefChatStore.getState().caseBriefId;
      if (briefId) useCreateProjectStore.getState().setGeneratedMeta(briefId, { ...generatedMetaRef.current });
      if (meta.photoQuery && briefId) requestCoverPhoto(briefId, meta.photoQuery);
    },
    [requestCoverPhoto]
  );
  const [iconShape, setIconShape] = useState<string | null>(null);
  const [iconColor, setIconColor] = useState<string | null>(null);

  const [isLaunching, setIsLaunching] = useState(false);
  const chatDimensionValues = useCaseBriefChatStore((s) => s.dimensionValues);
  const { data: orgDimensions } = useProjectDimensions();
  const sortedOrgDimensions = useMemo(
    () => (orgDimensions ? [...orgDimensions].sort((a, b) => a.sortOrder - b.sortOrder) : undefined),
    [orgDimensions]
  );

  const { languages } = useLanguages();
  const availableLanguages = useMemo(() => {
    const configuredValues = new Set(
      languages.map((l) => LANGUAGE_CODE_TO_VALUE[l.code] ?? l.code.toLowerCase()).filter(Boolean)
    );
    if (configuredValues.size === 0) return ALL_LANGUAGE_OPTIONS;
    return ALL_LANGUAGE_OPTIONS.filter((lang) => configuredValues.has(lang.value));
  }, [languages]);

  // The page has no language question: a new project starts in the interface language
  // when the organization offers it, and the project card changes it.
  const initialLanguage =
    uiLanguage && availableLanguages.some((option) => option.value === uiLanguage)
      ? uiLanguage
      : availableLanguages[0]?.value ?? "english";
  const [frameProgress, setFrameProgress] = useState<TFrameProgress>({
    activeSection: null,
    writingSection: null,
    coveredUpTo: null
  });
  const [detailsReading, setDetailsReading] = useState(false);
  // A document is marked read only once Leo has written the whole brief from it, minutes later, so the Vault
  // follows the chat's upload rows instead, which settle as soon as Leo has opened the file.
  const readingDocumentIds = useCaseBriefChatStore(
    useShallow((s) =>
      s.messages
        .filter((m) => m.content.includes('"status":"reading"'))
        .flatMap((m) => parseVaultItemsMessage(m.content) ?? [])
        .flatMap((item) => (item.status === "reading" && item.documentId ? [item.documentId] : []))
    )
  );
  // The project card can name the first version; empty keeps the default name.
  const [versionName, setVersionName] = useState("");
  const [launchConfirmOpen, setLaunchConfirmOpen] = useState(false);

  const hasQueryCaseBriefId = !!searchParams.get("caseBriefId");
  const [isRestoringBrief, setIsRestoringBrief] = useState(hasQueryCaseBriefId);

  const restoringRef = useRef(hasQueryCaseBriefId);
  const lastSavedRef = useRef<{ content: string; name: string; description: string } | null>(null);
  const syncedCaseBriefIdRef = useRef<number | null>(null);

  type CaseBriefResponse = {
    mdContent: string | null;
    language: string;
    visibility: "organization" | "private";
    name: string | null;
    description: string | null;
    dimensions: Record<string, string> | null;
    chatStep: string | null;
    pendingDimensionIndex: number | null;
    chatMessages: { id: string; role: "user" | "assistant"; content: string }[] | null;
    vectorStoreId: string | null;
    uploadedFiles:
      | { fileId?: string; fileName: string; documentId: number; fileSize: number; webUrl?: string }[]
      | null;
    completeness: CaseBriefCompleteness | null;
    generatingStatus: string | null;
    iconShape: string | null;
    iconColor: string | null;
    coverPhoto: TCoverPhoto | null;
  };

  const ensureCaseBriefPromiseRef = useRef<Promise<number> | null>(null);
  const ensureCaseBrief = useCallback(
    async (lang: string, mdContent: string): Promise<number> => {
      if (caseBriefId) return caseBriefId;
      const storeBriefId = useCaseBriefChatStore.getState().caseBriefId;
      if (storeBriefId) {
        setCaseBriefId(storeBriefId);
        return storeBriefId;
      }
      if (ensureCaseBriefPromiseRef.current) return ensureCaseBriefPromiseRef.current;
      const currentVectorStoreId = useCaseBriefChatStore.getState().vectorStoreId;
      const promise = $api
        .post<{ caseBriefId: number; visibility: "organization" | "private" }>(
          "/chat/create-project-chat/create-case-brief",
          {
            language: lang,
            mdContent,
            vectorStoreId: currentVectorStoreId || undefined,
            organizationId: currentOrganizationId || undefined,
            visibility: visibilityTouched.current ? visibility : undefined
          }
        )
        .then((res) => {
          const newId = res.data.caseBriefId;
          serverBriefRef.current ??= mdContent;
          setCaseBriefId(newId);
          useCaseBriefChatStore.getState().setCaseBriefId(newId);
          if (res.data.visibility) {
            visibilityTouched.current = true;
            setVisibility(res.data.visibility);
          }
          return newId;
        })
        .finally(() => {
          ensureCaseBriefPromiseRef.current = null;
        });
      ensureCaseBriefPromiseRef.current = promise;
      return promise;
    },
    [caseBriefId, currentOrganizationId, visibility]
  );

  useEffect(() => {
    if (caseBriefId === null) return;
    if (searchParams.get("caseBriefId") === String(caseBriefId)) return;
    const next = new URLSearchParams(searchParams);
    next.set("caseBriefId", String(caseBriefId));
    setSearchParams(next, { replace: true });
  }, [caseBriefId, searchParams, setSearchParams]);

  useEffect(() => {
    if (caseBriefCreated.current) return;
    caseBriefCreated.current = true;

    const queryCbId = searchParams.get("caseBriefId");
    if (queryCbId) {
      restoringRef.current = true;
      setIsRestoringBrief(true);
      const cbId = Number(queryCbId);
      setCaseBriefId(cbId);
      const storeState = useCaseBriefChatStore.getState();
      if (storeState.caseBriefId !== cbId) {
        storeState.reset();
      }
      useCaseBriefChatStore.getState().setCaseBriefId(cbId);
      $api
        .get<CaseBriefResponse>(`/chat/create-project-chat/case-brief/${cbId}`)
        .then((res) => {
          if (res.data.mdContent) {
            serverBriefRef.current = res.data.mdContent;
            setCaseBriefContent(res.data.mdContent);
          }
          if (res.data.language) {
            setLanguage(res.data.language);
            useCaseBriefChatStore.getState().setLanguage(res.data.language);
          }
          if (res.data.visibility) {
            visibilityTouched.current = true;
            setVisibility(res.data.visibility);
          }
          if (res.data.chatStep && res.data.chatStep !== "language") {
            const step = res.data.chatStep as ChatStep;
            useCaseBriefChatStore.getState().setStep(step);
            setChatStep(step);
          }
          if (res.data.iconShape) setIconShape(res.data.iconShape);
          if (res.data.iconColor) setIconColor(res.data.iconColor);
          if (res.data.coverPhoto) {
            coverPhotoQueryRef.current = res.data.coverPhoto.query;
            setCoverPhoto(res.data.coverPhoto);
          }
          if (typeof res.data.pendingDimensionIndex === "number") {
            useCaseBriefChatStore.getState().setPendingDimensionIndex(res.data.pendingDimensionIndex);
          }
          if (res.data.dimensions && typeof res.data.dimensions === "object") {
            useCaseBriefChatStore.getState().setDimensionValues(res.data.dimensions as Record<string, string>);
          }
          generatedMetaRef.current = {
            ...(useCreateProjectStore.getState().generatedMetaByCaseBrief[cbId] ?? { name: "", description: "" })
          };
          if (res.data.name) {
            setProjectName(res.data.name);
            useCaseBriefChatStore.getState().setProjectName(res.data.name);
            projectMetaGenerated.current = true;
          }
          if (res.data.description) {
            setProjectDescription(res.data.description);
            useCaseBriefChatStore.getState().setProjectDescription(res.data.description);
            projectMetaGenerated.current = true;
          }
          lastSavedRef.current = {
            content: res.data.mdContent ?? "",
            name: res.data.name ?? "",
            description: res.data.description ?? ""
          };
          syncedCaseBriefIdRef.current = cbId;
          if (res.data.vectorStoreId) {
            useCaseBriefChatStore.getState().setVectorStoreId(res.data.vectorStoreId);
          }
          if (res.data.completeness) {
            setCompleteness(res.data.completeness);
          }
          setInitialGeneratingStatus(res.data.generatingStatus ?? null);
          if (res.data.uploadedFiles && Array.isArray(res.data.uploadedFiles)) {
            useCaseBriefChatStore.getState().setUploadedFiles(res.data.uploadedFiles);
            for (const f of res.data.uploadedFiles) {
              addExternalDocument({
                documentId: f.documentId,
                fileName: f.fileName,
                status: "processing",
                fileSize: f.fileSize,
                ...(f.webUrl ? { webUrl: f.webUrl } : {})
              });
            }
          }
          if (res.data.chatMessages && Array.isArray(res.data.chatMessages) && res.data.chatMessages.length > 0) {
            const msgs = normalizeCaseBriefLanguageUserMessages(res.data.chatMessages);
            useCaseBriefChatStore.getState().setMessages(msgs);
            const parseJson = (content: string): { type?: string; templateKey?: string; content?: string } | null => {
              try {
                return JSON.parse(content);
              } catch {
                return null;
              }
            };
            let startIdx = -1;
            for (let i = 0; i < msgs.length; i++) {
              if (msgs[i].role !== "assistant") continue;
              if (parseJson(msgs[i].content)?.templateKey === "decisionQuestion") {
                startIdx = i;
                break;
              }
            }
            if (startIdx === -1) {
              // legacy drafts asked for the decision via the "Setup complete" transition message
              for (let i = msgs.length - 1; i >= 0; i--) {
                if (msgs[i].role !== "assistant") continue;
                const parsed = parseJson(msgs[i].content);
                if (
                  typeof parsed?.content === "string" &&
                  (parsed.content.includes("Setup complete") || parsed.content.includes("セットアップ完了"))
                ) {
                  startIdx = i;
                  break;
                }
              }
            }
            const chatOnly = startIdx >= 0 ? msgs.slice(startIdx + 1) : [];
            const derivedHistory: { role: "user" | "assistant"; content: string }[] = [];
            for (const m of chatOnly) {
              const parsed = parseJson(m.content);
              if (m.role === "user") {
                if (!parsed?.type) derivedHistory.push({ role: "user", content: m.content });
              } else if (parsed && !parsed.templateKey && typeof parsed.content === "string" && parsed.content) {
                derivedHistory.push({ role: "assistant", content: parsed.content });
              }
            }
            const deferredReply = useCaseBriefChatStore.getState().deferredDecisionReply;
            const deferredContent = deferredReply ? parseJson(deferredReply.content)?.content : null;
            if (deferredContent) derivedHistory.push({ role: "assistant", content: deferredContent });
            useCaseBriefChatStore.getState().setChatHistory(derivedHistory);
          }
        })
        .catch((err) => {
          console.error("[CaseBrief] restore failed:", err);
          toast.error(t("draftLoadFailed"));
          navigate(getRouteProjects(), { replace: true });
        })
        .finally(() => {
          restoringRef.current = false;
          setIsRestoringBrief(false);
        });
    } else {
      restoringRef.current = false;
      setIsRestoringBrief(false);
      // The chat already clears the store while initializing itself, and its
      // effects run before this one, so resetting unconditionally would wipe the
      // opening messages it just wrote. Only a leftover draft needs clearing.
      if (useCaseBriefChatStore.getState().caseBriefId !== null) {
        useCaseBriefChatStore.getState().reset();
      }
    }
  }, []);

  // The brief autosave is the only save that sends the brief, and the name/description autosave the
  // only one that sends those. Each is sent at once instead when the page is left or the analysis
  // launched within its debounce.
  const briefSavePendingRef = useRef(false);
  const saveBriefRef = useRef<() => Promise<void>>(async () => undefined);
  saveBriefRef.current = async () => {
    briefSavePendingRef.current = false;
    const cbId = await ensureCaseBrief(language, caseBriefContentRef.current);
    const fields = briefSaveFields();
    if (fields.mdContent === undefined) return;
    const res = await $api.patch<{ mdContent?: string }>("/chat/create-project-chat/update-case-brief-content", {
      caseBriefId: cbId,
      ...fields
    });
    // A newer copy (a background write, or more typing) may have arrived while this save was in
    // flight; the reply is then older than what's on screen and is left unused.
    const merged = res.data?.mdContent;
    const stillCurrent = caseBriefContentRef.current === fields.mdContent;
    if (stillCurrent && merged) {
      serverBriefRef.current = merged;
      // The merge kept sections written in the background; show them.
      if (merged !== fields.mdContent) setCaseBriefContent(merged);
    }
    lastSavedRef.current = {
      name: lastSavedRef.current?.name ?? "",
      description: lastSavedRef.current?.description ?? "",
      content: stillCurrent && merged ? merged : fields.mdContent
    };
  };

  useEffect(() => {
    if (!hasEdit) return;
    if (restoringRef.current) return;
    if (!caseBriefContent || caseBriefContent === getDefaultCaseBrief(language)) return;
    if (lastSavedRef.current?.content === caseBriefContent) return;
    briefSavePendingRef.current = true;
    const timer = setTimeout(() => {
      saveBriefRef.current().catch((err) => console.error("Failed to autosave case brief:", err));

      ensureCaseBrief(language, caseBriefContent).then((cbId) => {
        $api
          .post<CaseBriefCompleteness>("/chat/create-project-chat/completeness", {
            caseBriefContent,
            caseBriefId: cbId
          })
          .then((res) => setCompleteness(res.data));
      });

      if (!projectMetaGenerated.current) {
        ensureCaseBrief(language, caseBriefContent).then((cbId) => {
          $api
            .post<{ name: string | null; description: string | null; photoQuery: string | null }>(
              "/chat/create-project-chat/generate-project-meta",
              { caseBriefContent, language, caseBriefId: cbId }
            )
            .then((res) => applyGeneratedMeta(res.data))
            .catch((err) => console.error("Project meta generation failed:", err));
        });
      }
    }, 1000);
    return () => clearTimeout(timer);
  }, [caseBriefContent, language, caseBriefId, ensureCaseBrief, hasEdit, applyGeneratedMeta]);

  const metaSavePendingRef = useRef(false);
  const saveMetaRef = useRef<() => Promise<void>>(async () => undefined);
  saveMetaRef.current = async () => {
    metaSavePendingRef.current = false;
    const name = currentMetaRef.current.name;
    const description = currentMetaRef.current.description;
    const cbId = await ensureCaseBrief(language, caseBriefContentRef.current);
    await $api.patch("/chat/create-project-chat/update-case-brief-content", {
      caseBriefId: cbId,
      name: name || undefined,
      description: description || undefined
    });
    lastSavedRef.current = { content: lastSavedRef.current?.content ?? "", name, description };
  };

  useEffect(() => {
    if (!hasEdit) return;
    if (restoringRef.current) return;
    if (!projectName && !projectDescription) return;
    if (
      lastSavedRef.current &&
      lastSavedRef.current.name === (projectName || "") &&
      lastSavedRef.current.description === (projectDescription || "")
    ) {
      return;
    }
    metaSavePendingRef.current = true;
    const timer = setTimeout(() => {
      saveMetaRef.current().catch((err) => console.error("Failed to autosave project meta:", err));
    }, 1000);
    return () => clearTimeout(timer);
  }, [projectName, projectDescription, hasEdit]);

  useEffect(
    () => () => {
      if (briefSavePendingRef.current) saveBriefRef.current().catch(() => undefined);
      if (metaSavePendingRef.current) saveMetaRef.current().catch(() => undefined);
    },
    []
  );

  // Chat state (messages, step, files, dimensions) goes out as one save per burst of changes, since a
  // single click can post two messages and update a file row, and one save at a time, so an older
  // copy never lands after a newer one.
  const chatStateSaveRef = useRef({
    timer: null as ReturnType<typeof setTimeout> | null,
    inFlight: false,
    unmounted: false,
    changes: { chat: false, files: false, dimensions: false } as TChatStateChanges,
    // The store as of this draft's last change: leaving the page resets the store before the page
    // unmounts, so what is still unsent goes out from here.
    lastState: null as TChatStoreState | null
  });
  const sendChatStateRef = useRef<(changes: TChatStateChanges, state: TChatStoreState) => Promise<void>>(
    async () => undefined
  );
  sendChatStateRef.current = async (changes, state) => {
    if (state.caseBriefId !== caseBriefId) return;
    const withChat =
      changes.chat && state.messages.length > 0 && (!!caseBriefId || state.messages.some((m) => m.role === "user"));
    const dimensionSet = changes.dimensions && Object.values(state.dimensionValues).some((value) => !!value?.trim());
    const fields = {
      ...(withChat && {
        chatMessages: state.messages,
        chatStep: state.step,
        pendingDimensionIndex: state.pendingDimensionIndex
      }),
      ...(changes.files && { vectorStoreId: state.vectorStoreId ?? undefined, uploadedFiles: state.uploadedFiles }),
      ...(changes.dimensions && { dimensions: state.dimensionValues })
    };
    // Before the draft exists, only a conversation the user has joined or a dimension set on the form
    // creates it; the store clearing itself when the page opens does neither.
    if (Object.keys(fields).length === 0 || (!caseBriefId && !withChat && !dimensionSet)) return;
    const cbId = await ensureCaseBrief(language, caseBriefContentRef.current);
    await $api.patch("/chat/create-project-chat/update-case-brief-content", { caseBriefId: cbId, ...fields });
  };

  const flushChatState = useCallback(() => {
    const save = chatStateSaveRef.current;
    save.timer = null;
    const changes = save.changes;
    save.changes = { chat: false, files: false, dimensions: false };
    const state = save.unmounted ? save.lastState : useCaseBriefChatStore.getState();
    if (!state || restoringRef.current) return;
    save.inFlight = true;
    sendChatStateRef
      .current(changes, state)
      .catch((err) => console.error("Failed to autosave chat state:", err))
      .finally(() => {
        save.inFlight = false;
        const { chat, files, dimensions } = save.changes;
        if (chat || files || dimensions)
          save.timer = setTimeout(flushChatState, save.unmounted ? 0 : CHAT_STATE_SAVE_DELAY_MS);
      });
  }, []);

  const scheduleChatStateSave = useCallback(
    (changes: TChatStateChanges, state: TChatStoreState) => {
      const save = chatStateSaveRef.current;
      save.changes = {
        chat: save.changes.chat || changes.chat,
        files: save.changes.files || changes.files,
        dimensions: save.changes.dimensions || changes.dimensions
      };
      save.lastState = state;
      if (save.timer || save.inFlight) return;
      save.timer = setTimeout(flushChatState, CHAT_STATE_SAVE_DELAY_MS);
    },
    [flushChatState]
  );

  useEffect(
    () => () => {
      const save = chatStateSaveRef.current;
      save.unmounted = true;
      if (!save.timer) return;
      clearTimeout(save.timer);
      flushChatState();
    },
    [flushChatState]
  );

  useEffect(() => {
    if (!hasEdit) return;
    return useCaseBriefChatStore.subscribe((state, prev) => {
      if (restoringRef.current || state.caseBriefId !== caseBriefId) return;
      const changes = {
        chat:
          state.messages !== prev.messages ||
          state.step !== prev.step ||
          state.pendingDimensionIndex !== prev.pendingDimensionIndex,
        files: state.uploadedFiles !== prev.uploadedFiles || state.vectorStoreId !== prev.vectorStoreId,
        dimensions: state.dimensionValues !== prev.dimensionValues
      };
      if (changes.chat || changes.files || changes.dimensions) scheduleChatStateSave(changes, state);
    });
  }, [caseBriefId, hasEdit, scheduleChatStateSave]);

  // A draft created after the chat started gets everything said, uploaded and set so far.
  useEffect(() => {
    if (!hasEdit) return;
    if (!caseBriefId || restoringRef.current || syncedCaseBriefIdRef.current === caseBriefId) return;
    syncedCaseBriefIdRef.current = caseBriefId;
    const initial = useCaseBriefChatStore.getState();
    const changes = {
      chat: initial.messages.length > 0,
      files: !!initial.vectorStoreId || initial.uploadedFiles.length > 0,
      dimensions: Object.keys(initial.dimensionValues).length > 0
    };
    if (changes.chat || changes.files || changes.dimensions) scheduleChatStateSave(changes, initial);
  }, [caseBriefId, hasEdit, scheduleChatStateSave]);

  const caseBriefProgress = completeness?.overallScore ?? 0;

  // A document read after the name and description were filled in can change what the project is
  // about; only the fields the user hasn't edited follow it.
  const refreshGeneratedMeta = useCallback(() => {
    const untouched = (value: string, generated: string) => !value.trim() || value === generated;
    const { name, description } = currentMetaRef.current;
    if (
      !untouched(name, generatedMetaRef.current.name) &&
      !untouched(description, generatedMetaRef.current.description)
    )
      return;
    const brief = caseBriefContentRef.current;
    ensureCaseBrief(language, brief)
      .then((cbId) =>
        $api.post<{ name: string | null; description: string | null; photoQuery: string | null }>(
          "/chat/create-project-chat/generate-project-meta",
          { caseBriefContent: brief, language, caseBriefId: cbId }
        )
      )
      .then((res) => applyGeneratedMeta(res.data))
      .catch((err) => console.error("Project meta generation failed:", err));
  }, [ensureCaseBrief, language, applyGeneratedMeta]);

  const handleChatFileUploaded = useCallback(
    (chatFile: ChatUploadedFile) => {
      addExternalDocument({
        documentId: chatFile.documentId,
        fileName: chatFile.fileName,
        status: "processing",
        fileSize: chatFile.fileSize
      });
    },
    [addExternalDocument]
  );

  const handleLanguageSelected = useCallback(
    async (lang: string, options?: { deferDraft?: boolean }) => {
      const previous = language;
      setLanguage(lang);

      if (restoringRef.current) return;

      // No draft exists yet, so the empty template simply follows the language.
      if (options?.deferDraft) {
        setCaseBriefContent(getDefaultCaseBrief(lang));
        return;
      }
      if (lang === previous) return;

      // The draft keeps everything written so far, including sections still being read from the
      // documents; the server translates the headings and labels and sends the brief back.
      const fields = briefSaveFields();
      ensureCaseBrief(lang, caseBriefContentRef.current)
        .then((cbId) =>
          $api.patch<{ mdContent?: string }>("/chat/create-project-chat/update-case-brief-language", {
            caseBriefId: cbId,
            language: lang,
            ...fields
          })
        )
        .then((res) => {
          const translated = res.data.mdContent;
          if (!translated) return;
          serverBriefRef.current = translated;
          // Typing that happened meanwhile stays; the next autosave merges it onto the translated copy.
          const typedMeanwhile = fields.mdContent !== undefined && caseBriefContentRef.current !== fields.mdContent;
          if (!typedMeanwhile) {
            setCaseBriefContent(translated);
            lastSavedRef.current = {
              name: lastSavedRef.current?.name ?? "",
              description: lastSavedRef.current?.description ?? "",
              content: translated
            };
          }
        })
        .catch((err) => console.error("Failed to update language:", err));
    },
    [language, ensureCaseBrief, setCaseBriefContent, briefSaveFields]
  );

  const handleVisibilitySelected = useCallback(
    (next: "organization" | "private") => {
      visibilityTouched.current = true;
      setVisibility(next);

      if (restoringRef.current) return;

      ensureCaseBrief(language, caseBriefContent)
        .then((cbId) => {
          $api
            .patch("/chat/create-project-chat/update-case-brief-visibility", {
              caseBriefId: cbId,
              visibility: next
            })
            .catch((err) => console.error("Failed to update visibility:", err));
        })
        .catch((err) => console.error("Failed to ensure case brief on visibility select:", err));
    },
    [ensureCaseBrief, language, caseBriefContent]
  );

  const handleEnsureCaseBrief = useCallback(
    () => ensureCaseBrief(language, caseBriefContent),
    [ensureCaseBrief, language, caseBriefContent]
  );

  const handleChatDocumentUpdate = useCallback(
    (content: string, sections?: TBriefSections) => {
      if (!sections) {
        serverBriefRef.current = content;
        setCaseBriefContent(content);
        return;
      }
      serverBriefRef.current = withSectionsFrom(serverBriefRef.current ?? content, content, sections);
      setCaseBriefContent(withSectionsFrom(caseBriefContentRef.current, content, sections));
    },
    [setCaseBriefContent]
  );
  const getBriefForSave = useCallback(
    () => ({ content: caseBriefContentRef.current, base: documentReading ? serverBriefRef.current : null }),
    [documentReading]
  );

  // The app header renders outside this page, so the project identity it shows
  // (icon + name) is mirrored into the store on every change, including restores.
  useEffect(() => {
    const store = useCaseBriefChatStore.getState();
    store.setProjectName(projectName || null);
    store.setIcon(iconShape, iconColor);
    store.setLogoUrl(logoBase64 ?? coverPhoto?.url ?? null);
  }, [projectName, iconShape, iconColor, logoBase64, coverPhoto]);

  const createProject = async (templateSlug: string) => {
    const documentIds = uploadedDocuments.length > 0 ? uploadedDocuments.map((doc) => doc.documentId) : undefined;
    const chatDimensionValues = useCaseBriefChatStore.getState().dimensionValues;
    const mergedDimensionValues = {
      ...(projectData.dimensionValues ?? {}),
      ...chatDimensionValues
    };

    const newId = await addProject({
      name: projectName || "Untitled Project",
      projectTemplateSlug: templateSlug,
      description: projectDescription || undefined,
      iconShape: iconShape || undefined,
      iconColor: iconColor || undefined,
      logoBase64: logoBase64 || undefined,
      projectInstructions: projectData.projectInstructions || undefined,
      language,
      visibility,
      chaptersInstructions: projectData.chaptersInstructions || {},
      documentIds,
      caseBriefId: caseBriefId || undefined,
      presentationPrompt: projectData.presentationPrompt || undefined,
      dimensionValues: Object.keys(mergedDimensionValues).length > 0 ? mergedDimensionValues : undefined
    });

    if (versionName.trim()) {
      await $api
        .patch(`/projects/${newId}`, { versionDisplayName: versionName.trim() })
        .catch((err) => console.error("Failed to name the first version:", err));
    }

    queryClient.invalidateQueries({ queryKey: [GET_PROJECTS_LIST_KEY], refetchType: "all" });

    const taskId = await triggerAutoSetup(newId, selectedModel, true);
    if (taskId) {
      setSetupTask(newId, taskId);
    }

    navigate(getRouteProjectDetails(newId));
    clearDocuments();
    useCaseBriefChatStore.getState().reset();
    if (caseBriefId !== null) clearProjectData(caseBriefId);
  };

  const handleLaunchAnalysis = async () => {
    setIsLaunching(true);

    try {
      await chatRef.current?.finishReadingBeforeLaunch();
      if (briefSavePendingRef.current) await saveBriefRef.current();

      // Claude-code-generation projects carry no template: the generation invents
      // the chapter structure itself, so template selection is skipped entirely.
      let templateSlug = claudeGeneration ? "" : projectData.projectTemplateSlug;

      if (!claudeGeneration && !templateSlug) {
        // Read from the ref: finishing the read above may have just written more of the brief.
        const latestCaseBrief = caseBriefContentRef.current;
        const cbId = await ensureCaseBrief(language, latestCaseBrief);
        const templateResponse = await $api.post<{ templateSlug: string }>(
          "/chat/create-project-chat/select-template",
          { language, caseBriefContent: latestCaseBrief, caseBriefId: cbId }
        );
        templateSlug = templateResponse.data.templateSlug;
      }

      if (isLabs) {
        setProjectData({
          ...projectData,
          projectTemplateSlug: templateSlug,
          name: projectName,
          description: projectDescription
        });
      }

      await createProject(templateSlug);
    } catch (error) {
      console.error("Failed to launch analysis:", error);
      toast.error(t("launch.failed"));
    } finally {
      setIsLaunching(false);
    }
  };

  // `language` always holds a value ("english" by default), so a language is always
  // selected — the default counts toward setup from the start, no explicit pick required.
  const languageChosen = !!language;

  const briefStatus = !projectName.trim() || !languageChosen ? "empty" : essentialsCovered ? "complete" : "incomplete";

  // With documents, the Case Frame reads them once the decision is answered in the chat.
  const decisionPending =
    !!documentReading && uploadedDocuments.length > 0 && (chatStep === "files" || chatStep === "decision");

  const answerDimensionInChat = useDebounce((id: number, value: string) => {
    chatRef.current?.answerDimensionViaPill(id, value);
  }, 500);

  const handleManualDimensionChange = (id: number, value: string) => {
    useCaseBriefChatStore.getState().patchDimensionValue(id, value);
    answerDimensionInChat(id, value);
  };

  const organizationTags = useOrganizationTags({
    organizationId: currentOrganizationId,
    dimensions: sortedOrgDimensions ?? [],
    values: chatDimensionValues,
    onValueCleared: (id) => useCaseBriefChatStore.getState().patchDimensionValue(id, "")
  });

  const chatElement = (
    <CreateProjectChat
      chatRef={chatRef}
      caseBriefId={caseBriefId}
      isRestoring={isRestoringBrief}
      readOnly={readOnly}
      externalLanguage={chatStep === "language" ? null : language}
      progress={caseBriefProgress}
      currentBriefContent={caseBriefContent}
      initialGeneratingStatus={initialGeneratingStatus}
      onDocumentUpdate={handleChatDocumentUpdate}
      getBriefForSave={getBriefForSave}
      onFileUploaded={handleChatFileUploaded}
      onLanguageSelected={handleLanguageSelected}
      onEnsureCaseBrief={handleEnsureCaseBrief}
      onStepChange={setChatStep}
      uploadedDocuments={uploadedDocuments}
      onAddWebLink={(url) => addWebLink(url, language)}
      onUploadFiles={(files) => uploadDocumentsToDraft(files, language)}
      documentReadingEnabled={documentReading}
      documentReadingFlagLoading={documentReading === undefined}
      onProjectMeta={applyGeneratedMeta}
      onDocumentsRead={refreshGeneratedMeta}
      onEssentialsCovered={handleEssentialsCovered}
      onRemoveDocument={removeDocument}
      onLaunchAnalysis={handleLaunchAnalysis}
      briefStatus={briefStatus}
      isLaunching={isLaunching}
      leoPanel={{
        language: initialLanguage,
        onRequestLaunch: () => setLaunchConfirmOpen(true),
        onFrameProgress: setFrameProgress,
        onDetailsReading: setDetailsReading
      }}
      onReset={() => {
        const resetLanguage = availableLanguages.length === 1 ? availableLanguages[0].value : "english";
        const resetBrief = getDefaultCaseBrief(resetLanguage);
        setCaseBriefContent(resetBrief);
        setCompleteness(null);
        setEssentialsCovered(false);
        setProjectName("");
        setProjectDescription("");
        setLanguage(resetLanguage);
        setIconShape(null);
        setIconColor(null);
        setLogoBase64(null);
        setCoverPhoto(null);
        coverPhotoQueryRef.current = null;
        projectMetaGenerated.current = false;
        clearDocuments();
        serverBriefRef.current = resetBrief;
        lastSavedRef.current = { content: resetBrief, name: "", description: "" };
        // The chat cleared the store along with its draft id; the draft itself stays.
        if (caseBriefId) useCaseBriefChatStore.getState().setCaseBriefId(caseBriefId);
        if (caseBriefId) {
          $api
            .patch("/chat/create-project-chat/update-case-brief-content", {
              caseBriefId,
              mdContent: resetBrief,
              name: "",
              description: "",
              dimensions: {},
              chatStep: "language",
              pendingDimensionIndex: 0,
              chatMessages: [],
              uploadedFiles: [],
              vectorStoreId: "",
              completeness: null,
              iconShape: null,
              iconColor: null,
              coverPhoto: null
            })
            .then(() => {
              queryClient.invalidateQueries({ queryKey: [GET_PROJECTS_LIST_KEY], refetchType: "all" });
            })
            .catch((err) => console.error("Failed to reset case brief:", err));
        }
        useCaseBriefChatStore.getState().setProjectName(null);
        useCaseBriefChatStore.getState().setProjectDescription(null);
      }}
    />
  );

  return (
    <>
      <NewProjectView
        readOnly={readOnly}
        restoring={isRestoringBrief}
        chat={chatElement}
        project={{
          name: projectName,
          description: projectDescription,
          // The documents' preview brings the name and description; the card waits for it unless one is typed.
          pending: detailsReading && !projectName.trim() && !projectDescription.trim(),
          onNameChange: setProjectName,
          onDescriptionChange: setProjectDescription,
          picture: logoBase64
            ? { url: logoBase64 }
            : coverPhoto
            ? { url: coverPhoto.url, credit: coverPhoto.author }
            : null,
          pictureRemovable: !!logoBase64,
          onPictureChange: setLogoBase64,
          language,
          languageOptions: availableLanguages.map((option) => ({
            value: option.value,
            label: NATIVE_LANGUAGE_LABELS[option.value] ?? option.label
          })),
          versionName,
          onVersionNameChange: setVersionName,
          onLanguageChange: (lang) => {
            if (chatStep === "language") chatRef.current?.selectLanguage(lang);
            else handleLanguageSelected(lang);
          },
          visibility,
          onVisibilityChange: handleVisibilitySelected,
          dimensions: sortedOrgDimensions ?? [],
          dimensionValues: chatDimensionValues,
          onDimensionChange: handleManualDimensionChange,
          tagManagement: hasAdminPrivileges(role) ? organizationTags : undefined
        }}
        vault={{
          documents: uploadedDocuments,
          pendingNames: pendingUploadNames,
          onAddFiles: (files) => void uploadDocumentsToDraft(files, language),
          onAddLink: (url) => void addWebLink(url, language).catch(() => toast.error(t("failedUpload", { name: url }))),
          onOpen: (documentId) => {
            const document = uploadedDocuments.find((doc) => doc.documentId === documentId);
            if (!document) return;
            const filePath = caseBriefId === null ? null : caseBriefDocumentPath(caseBriefId, documentId);
            openVaultDocument(document, filePath).catch(() =>
              toast.error(t("failedOpen", { name: document.webUrl ?? removeTimestampPrefix(document.fileName) }))
            );
          },
          onRemove: removeDocument,
          readingIds: readingDocumentIds
        }}
        frame={{
          brief: caseBriefContent,
          onChange: setCaseBriefContent,
          completeness,
          progress: caseBriefProgress,
          // The decision question comes before the walkthrough, and it is Section 1's.
          activeSection: frameProgress.activeSection ?? (chatStep === "decision" ? 1 : null),
          writingSection: frameProgress.writingSection,
          coveredUpTo: frameProgress.coveredUpTo,
          showTemplatePrompts: !!documentReading,
          caseBriefId,
          lang: contentLng
        }}
        launch={{
          status: briefStatus,
          decisionPending,
          isLaunching,
          confirmOpen: launchConfirmOpen,
          onConfirmOpenChange: setLaunchConfirmOpen,
          onLaunch: () => {
            setLaunchConfirmOpen(false);
            void handleLaunchAnalysis();
          }
        }}
      />
    </>
  );
}
